import * as React from "react";
import { clsx } from "clsx";
import { useEffect, useRef, useState } from "react";
import Alpine from "alpinejs";
import useAnimationEnd from "@arctheme-hooks/useAnimationEnd";
import { TextField } from "@arctheme-components/elements/TextField/TextField";
import { useNavigationParse } from "@arctheme-hooks/useNavigationParse";
import { Collapsible } from "@arctheme-components/elements/Collapsible/Collapsible";

export const MobileMenu = ({ link_list, settings }) => {
  const menuLinks = link_list[0]?.links;
  const [menuState, setMenuState] = useState({
    menuAnimClass: "",
    accountAnimClass: "",
    isAccountVisible: false,
    isMenuVisible: false,
  });
  const [expandedSections, setExpandedSections] = useState({});
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);

  const mobileMegamenuBlocks = {}
  settings.blocks?.filter(block => block.type === "mobile_megamenu")?.forEach(block => {
    mobileMegamenuBlocks[block.settings.attach_to] = block
  });

  // Mobile icon sizes
  const mobileIconSize = settings.section?.mobile_icon_size || 24;
  
  // Get search block settings or fall back to main section settings
  const searchBlock = settings.blocks.find(block => block.type === "search_bar") || {};
  
  // Merge settings with fallbacks
  const searchSettings = {
    enable_searchbar: searchBlock.enable_searchbar !== undefined ? searchBlock.enable_searchbar : settings.section.enable_searchbar,
    search_bar_position_mobile: searchBlock.search_bar_position_mobile || settings.section.header_search_bar_position_mobile || "below_header",
    search_placeholder: searchBlock.search_placeholder || "Search...",
    search_icon: searchBlock.search_icon || settings.section.search_icon || "icon-magnifying-glass-thin",
  };
  
  // Set CSS variables for mobile menu
  const mobileMenuStyles = {
    '--mobile-icon-size': `${mobileIconSize}px`,
    '--cart-counter-radius': `${settings.section?.cart_counter_radius || 12}px`,
    '--cart-counter-bg-color': settings.section?.cart_counter_bg_color || '#000',
    '--cart-counter-text-color': settings.section?.cart_counter_text_color || '#fff'
  } as React.CSSProperties;

  const useAccordions = true;
  const slidingDivRef = useRef(null);
  const updateMenuState = (updates) =>
    setMenuState((prevState) => ({ ...prevState, ...updates }));
  const dispatchCustomEvent = (eventName, detail = { message: "" }) => {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  };
  const [inputValue, setInputValue] = useState("");

  let customerInitials = "A";

  if (settings.globals?.is_customer_logged_in) {
    customerInitials = settings.globals.customer_name.trim()[0];
  }

  useAnimationEnd(slidingDivRef, () => {
    if (
      menuState.isAccountVisible &&
      menuState.accountAnimClass == "slide-out-to-right"
    ) {
      updateMenuState({
        isAccountVisible: false,
      });

      dispatchCustomEvent("toggle-display-close", {
        message: "header-account",
      });
    }
  });

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    // Update predictive search if available
    const predictiveInput = document.getElementById("predictive-search-input");
    if (predictiveInput) {
      predictiveInput.value = value;
      const event = new Event("input", { bubbles: true });
      predictiveInput.dispatchEvent(event);
    }
  };
  
  const toggleSection = (index) => {
    setExpandedSections((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const menuItemClicked = (dataId) => {
    if (!useAccordions) {
      updateMenuState({
        menuAnimClass: "slide-out-left",
      });

      Alpine.store("navigation").setMenuId(`header-${dataId}`);
    }
  };

  const handleSubMenuOpen = () => {
    updateMenuState({
      menuAnimClass: "slide-out-left",
      isAccountVisible: false,
      isMenuVisible: true,
    });
  };

  const handleSubMenuClose = () => {
    updateMenuState({
      menuAnimClass: "slide-in-left",
      accountAnimClass: "slide-out-to-right",
      isMenuVisible: true,
      isAccountVisible: false,
    });

    menuState.isMenuVisible = true;
    menuState.menuAnimClass = "slide-in-left";
    onMenuClick();
    dispatchCustomEvent("megamenu-close");
  };

  const onMenuClick = () => {
    dispatchCustomEvent("searchbar-close");

    updateMenuState({
      menuAnimClass: "slide-in-left",
      accountAnimClass: "slide-out-left",
      isMenuVisible: true,
      isAccountVisible: false,
    });

    dispatchCustomEvent("mobile-menu-open");
  };

  const onCloseClick = () => {
    updateMenuState({
      accountAnimClass: "slide-out-to-right",
      menuAnimClass: "slide-out-left",
      isAccountVisible: false,
      isMenuVisible: false,
    });

    dispatchCustomEvent("toggle-display-close", { message: "header-account" });
    dispatchCustomEvent("mobile-menu-closed");
    dispatchCustomEvent("megamenu-close");
    setExpandedSections({});
  };

  const handleSearchbarOpen = () => {
    updateMenuState({
      accountAnimClass: "",
      menuAnimClass: "",
      isAccountVisible: false,
      isMenuVisible: false,
    });

    dispatchCustomEvent("toggle-display-close", { message: "header-account" });
    dispatchCustomEvent("mobile-menu-closed");
    dispatchCustomEvent("megamenu-close");
    setExpandedSections({});
  };

  const handleBackClick = () => {
    updateMenuState({
      accountAnimClass: "slide-out-to-right",
      menuAnimClass: "slide-in-left",
      isMenuVisible: true,
    });
  };

  const handleAccountClick = () => {
    setIsAccountMenuOpen(!isAccountMenuOpen);
  };

  const searchInputUpdated = (e) => {
    setInputValue(e.detail.value);
  };

  // Function to toggle minicart
  const toggleMinicart = () => {
    if (window.Alpine && window.Alpine.store('global')) {
      window.Alpine.store('global').toggleMinicart();
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(inputValue)}`;
    }
  };

  useEffect(() => {
    const fetchCart = async (event) => {
      try {
        const response = await fetch(`/cart.js`);
        const cartData = await response.json();
        const updatedItemCount = cartData.item_count || 0;
        setCartItemCount(updatedItemCount);
      } catch (error) {
        console.error("Error fetching cart:", error);
      }
    };

    document.addEventListener("liquid-ajax-cart:request-end", function(event: any) {
      const {requestState, cart, previousCart, sections} = event.detail;
      fetchCart({ detail: {} });
    });

    window.addEventListener("megamenu-menu-close", handleSubMenuClose);
    window.addEventListener("searchInputUpdated", searchInputUpdated);
    window.addEventListener("megamenu-menu-open", handleSubMenuOpen);
    window.addEventListener("searchbar-open", handleSearchbarOpen);
    
    // Initial cart fetch
    fetchCart({ detail: {} });
    
    return () => {
      window.removeEventListener("megamenu-menu-close", handleSubMenuClose);
      window.removeEventListener("searchInputUpdated", searchInputUpdated);
      window.removeEventListener("megamenu-menu-open", handleSubMenuOpen);
      window.removeEventListener("searchbar-open", handleSearchbarOpen);
      window.removeEventListener('liquid-ajax-cart:request-end', () => {});
    };
  }, []);

  // Filter blocks to find the one with type 'account_popup'
  const accountPopupSettings = settings.blocks.find(
    (block) => block.type === "account_popup",
  );

  // Should show search in menu based on mobile position setting
  const showSearchInMenu = searchSettings.enable_searchbar && searchSettings.search_bar_position_mobile === "inside_menu";
  
  return (
    <div className="mobile-menu-container" style={mobileMenuStyles}>
      <nav
        aria-label="Mobile Navigation Overlay"
        className={`navigation-overlay ${clsx({
          open: menuState.isMenuVisible,
          closed: !menuState.isMenuVisible,
        })}`}
        id="mobile-nav"
        role="navigation"
      >
        <span
            aria-label={menuState.isMenuVisible ? "Close Mobile Navigation" : "Open Mobile Navigation"}
            onClick={menuState.isMenuVisible ? onCloseClick : onMenuClick}
        >
          {!menuState.isMenuVisible ? (
            <span className="flex" aria-hidden="true" onClick={onMenuClick}>
              <i
                className={`mobile_menu-icon ${settings.section.mobile_menu_icon}`}
              ></i>
            </span>
          ) : (
            <span className="flex" aria-hidden="true" onClick={onCloseClick}>
              <i
                className={`mobile_menu-icon ${settings.section.mobile_menu_icon_close}`}
              ></i>
            </span>
          )}
        </span>
      </nav>

      <div 
        className={`absolute w-full h-screen z-10 bg-black opacity-[0.16] ${menuState.isMenuVisible ? "" : "hidden"}`}
        style={{
          margin: `0 -${settings.section.padding_left_right_mobile}px`,
          width: `calc(100% + ${settings.section.padding_left_right_mobile * 2}px)`,
        }}
        onClick={onCloseClick}
      ></div>

      <div
        className={`relative menu-list w-full ${menuState.menuAnimClass} ${menuState.isMenuVisible ? "" : "hidden"}`}
        style={{
          margin: `0 -${settings.section.padding_left_right_mobile}px`,
          width: `calc(100% + ${settings.section.padding_left_right_mobile * 2}px)`,
        }}
      >
        <ul
          id="mobile-menu-list"
          role="menu"
          className={`bg-white py-4 px-5 rounded-2xl mt-4 relative z-20 overflow-auto max-h-[calc(100dvh-142px)]`}
          style={{
            marginLeft: `${settings.section.padding_left_right_mobile}px`,
            marginRight: `${settings.section.padding_left_right_mobile}px`
          }}
        >
          {showSearchInMenu && (
            <div className="mobile-menu-items flex flex-col p-[16px] gap-[8px]">
              <form 
                action="/search" 
                method="get" 
                role="search"
                className="mobile-search-form"
                onSubmit={handleSearchSubmit}
              >
                <div className="mobile-search-input-container flex items-center">
                  <TextField
                    id="mobile-search"
                    name="q"
                    type="search" 
                    placeholder={searchSettings.search_placeholder}
                    value={inputValue}
                    onChange={handleInputChange}
                    className="mobile-search-input"
                    icon={searchSettings.search_icon}
                    reverseIcon={true}
                  />
                </div>
              </form>
            </div>
          )}

          {menuLinks.map((link, index) => {
            const text = useNavigationParse(link.title);
            const megamenuBlock = mobileMegamenuBlocks[link.title];
            const links = megamenuBlock?.links || link.links || [];
            const isOpen = expandedSections[index] && links?.length > 0;
            const mainParentClass = isOpen ? "open" : "closed";

            return (
              <li
                key={index}
                className={`mobile-menu-item menu-item flex flex-col ${mainParentClass}`}
                onClick={() => {
                  if (useAccordions) {
                    toggleSection(index);
                  } else {
                    menuItemClicked(link.title.toLowerCase().replace(" ", "-"));
                  }
                }}
              >
                <div className="flex w-full items-center justify-between cursor-pointer py-3">
                  <a href={link.url} className={`h4`}>
                    <span className={`inline-flex justify-center gap-[8px]`}>
                      {text.text}
                    </span>
                  </a>

                  {links?.length > 0 && isOpen && (
                    <i className="text-2xl icon-chevron-up"></i>
                  )}

                  {links?.length > 0 && !isOpen && (
                    <i className="text-2xl icon-chevron-right"></i>
                  )}
                </div>

                {isOpen && links?.length > 0 && (
                  <ul className="w-full flex flex-col gap-6">
                    {links.map((subLink, subIndex) => {
                      const subDrillIndex = (index + 1 * 100) + subIndex;
                      const isSubOpen = expandedSections[subDrillIndex] && subLink.links?.length > 0;

                      const menuIcons = {}
                      megamenuBlock.settings[`level2_menu_icons`]
                        ?.split(',')
                        ?.forEach(item => {
                            const [menu, icon] = item.split('|');
                            menuIcons[menu?.trim()] = icon?.trim();
                        });

                      return (
                        <li
                          key={subDrillIndex}
                          className="mobile-menu-sub-item"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSection(subDrillIndex);
                          }}
                        >
                          <div className="flex w-full items-center justify-between cursor-pointer py-2">
                            <a
                              href={subLink.url}
                              className={`b3--bold flex items-center gap-3`}
                            >
                              {menuIcons[subLink.title] && (
                                <i className={`text-2xl ${menuIcons[subLink.title]}`}></i>
                              )}

                              <span>{subLink.title}</span>
                            </a>

                            {subLink.links?.length > 0 && isSubOpen && (
                              <i className="text-2xl icon-chevron-up"></i>
                            )}

                            {subLink.links?.length > 0 && !isSubOpen && (
                              <i className="text-2xl icon-chevron-right"></i>
                            )}
                          </div>

                          {isSubOpen && subLink.links?.length > 0 && (
                            <ul className="py-4 flex flex-col gap-6">
                              {subLink.links.map((subSubLink, subSubIndex) => (
                                <li>
                                  <a
                                    key={subSubIndex}
                                    href={subSubLink.url}
                                    className="b3--bold mb-2 inline-block"
                                  >
                                    {subSubLink.title}
                                  </a>

                                  {subSubLink.links?.length > 0 && (
                                    <ul className="flex flex-col gap-2">
                                      {subSubLink.links.map((subSubSubLink, subSubSubIndex) => (
                                        <li key={subSubSubIndex}>
                                          <a
                                            href={subSubSubLink.url}
                                            className="py-2 b4 inline-block"
                                          >
                                            {subSubSubLink.title}
                                          </a>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}

          <li className="hidden mobile-menu-items flex-col p-[16px] gap-[8px]">
            {!settings.globals?.is_customer_logged_in && (
              <a href={"/account/login"} className={"button button-tertiary"}>
                Log In / Sign up
              </a>
            )}
            {settings.globals?.is_customer_logged_in && (
              <>
                <div
                  className={`border border-black ${isAccountMenuOpen ? "rounded-[16px]" : "rounded-[40px]"} py-1 pl-2 pr-4 flex flex-col items-center justify-between cursor-pointer h-auto`}
                  onClick={handleAccountClick}
                >
                  <div className="flex w-full justify-between items-center">
                    <div className="flex items-center gap-[8px]">
                      <div className="account-default-popup account-icon antialiased no-underline cursor-pointer">
                        <div className="flex items-center justify-center text-[16px] bg-[#99FF99] rounded-full text-black font-semibold bf w-[30px] h-[30px] uppercase">
                          <span>{customerInitials}</span>
                        </div>
                      </div>
                      <div className="flex flex-col h5">
                        {isAccountMenuOpen ? "Your Account" : "My Account"}
                      </div>
                    </div>

                    <i
                      className={`icon ${isAccountMenuOpen ? "icon-caret-up-thin" : "icon-caret-down-thin"}`}
                    />
                  </div>

                  {isAccountMenuOpen && (
                    <ul className="account-dropdown-menu mx-[40px] w-full py-[18px] flex gap-y-[16px] flex-col">
                      {accountPopupSettings && accountPopupSettings.links && 
                        accountPopupSettings.links.map((link, index) => (
                          <li key={index} className="mobile-menu-sub-item">
                            <a href={`${link.url}`} className="h5">
                              {link.title}
                            </a>
                          </li>
                        ))}

                      <li className="mobile-menu-sub-item">
                        <a href="/account/logout" className="h5 flex items-center">
                          <span className="underline">Log Out</span>
                          <span className="ml-1 icon-curved-arrow-up-right text-[17px]"></span>
                        </a>
                      </li>
                    </ul>
                  )}
                </div>
              </>
            )}  
          </li>
        </ul>
      </div>

      <div
        ref={slidingDivRef}
        className={`header-mobile-account ${menuState.accountAnimClass} ${menuState.isAccountVisible ? "" : "force-hidden"}`}
      >
        <div
          className="b2 font-medium mobile-menu-item back-button"
          onClick={handleBackClick}
        >
          <i className="icon-chevron-left back-button-icon"></i>
          Back
        </div>
      </div>
    </div>
  );
};
